import React, { useMemo } from 'react';
import { Button } from 'antd';
import DashboardModal from './DashboardModal';
import './oneButton-css/SunnovaSystemErrorModal.css';

/**
 * React parity port of Angular `app-sunnova-system-error` (NzModalRef-hosted body).
 *
 * Source of truth: `scrap/sunnova-system-error/sunnova-system-error.component.{html,ts}`.
 * - Renders four labeled rows (message / info / reason / code) on a 25% / 75% grid
 *   matching Bootstrap `col-3` + `col-9` used in the Angular template.
 * - When `data` is missing, shows the centered "No data available" empty state.
 * - Accepts the same input shape as the Angular `@Input() data` (object **or**
 *   JSON string); `ngOnInit` parses strings via `JSON.parse`, we mirror that here.
 */
export type SunnovaSystemErrorPayload = {
    message?: React.ReactNode;
    info?: React.ReactNode;
    reason?: React.ReactNode;
    code?: React.ReactNode;
};

type Props = {
    open: boolean;
    onClose: () => void;
    /** Raw data — accepts either an object or a JSON string for Angular parity. */
    data: SunnovaSystemErrorPayload | string | null | undefined;
    theme?: 'dark' | 'light';
    zIndex?: number;
    /** Modal title; defaults to Angular caller value `Error`. */
    title?: React.ReactNode;
};

/**
 * Mirrors Angular `ngOnInit`:
 *   `this.data = (typeof this.data === 'object') ? this.data : JSON.parse(this.data);`
 * Returns `null` for empty/invalid payloads so the empty-state branch renders.
 */
function parseAngularErrorData(
    input: SunnovaSystemErrorPayload | string | null | undefined,
): SunnovaSystemErrorPayload | null {
    if (input == null) return null;
    if (typeof input === 'object') return input as SunnovaSystemErrorPayload;
    try {
        const parsed = JSON.parse(String(input));
        return parsed && typeof parsed === 'object' ? (parsed as SunnovaSystemErrorPayload) : null;
    } catch {
        return null;
    }
}

const SunnovaSystemErrorModal: React.FC<Props> = ({
    open,
    onClose,
    data,
    theme = 'dark',
    zIndex,
    title = 'Error',
}) => {
    const parsed = useMemo(() => parseAngularErrorData(data), [data]);

    return (
        <DashboardModal
            title={title}
            open={open}
            onClose={onClose}
            theme={theme}
            width={620}
            zIndex={zIndex}
            destroyOnClose
            maskClosable
            className="obd-sunnova-system-error-modal"
            rootClassName={`obd-sunnova-system-error-root obd-theme-${theme}`}
            footer={null}
        >
            <div id="contents" className="obd-sunnova-error">
                {parsed ? (
                    <div className="obd-sunnova-error__rows">
                        <div className="obd-sunnova-error__row">
                            <div className="obd-sunnova-error__label">message</div>
                            <div
                                className="obd-sunnova-error__value obd-sunnova-error__value--message"
                                style={{ lineHeight: 1 }}
                            >
                                {parsed.message ?? ''}{' '}
                            </div>
                        </div>
                        <div className="obd-sunnova-error__row">
                            <div className="obd-sunnova-error__label">info</div>
                            <div className="obd-sunnova-error__value">
                                <p>{parsed.info ?? ''}</p>
                            </div>
                        </div>
                        <div className="obd-sunnova-error__row">
                            <div className="obd-sunnova-error__label">reason</div>
                            <div className="obd-sunnova-error__value">{parsed.reason ?? ''} </div>
                        </div>
                        <div className="obd-sunnova-error__row">
                            <div className="obd-sunnova-error__label">code</div>
                            <div className="obd-sunnova-error__value">{parsed.code ?? ''} </div>
                        </div>
                    </div>
                ) : (
                    <div className="obd-sunnova-error__empty">No data available</div>
                )}
            </div>
        </DashboardModal>
    );
};

export default SunnovaSystemErrorModal;
